/**
 * Documents Tab Component
 * Displays uploaded documents
 */

import React from 'react';
import {
  Box,
  Typography,
  Chip,
  IconButton,
  Tooltip,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Download as DownloadIcon,
  Visibility as ViewIcon,
  CheckCircle as VerifiedIcon,
  Pending as PendingIcon,
} from '@mui/icons-material';
import type { GhostApplicationDetail } from '../../../../../types/ghostVerification';

interface DocumentsTabProps {
  application: GhostApplicationDetail;
}

const documentTypeLabels: Record<string, string> = {
  id_card: 'ID Card',
  passport: 'Passport',
  driver_license: 'Driver License',
  proof_of_address: 'Proof of Address',
  background_check: 'Background Check',
  other: 'Other',
};

export const DocumentsTab: React.FC<DocumentsTabProps> = ({ application }) => {
  const theme = useTheme();

  if (!application.documents || application.documents.length === 0) {
    return (
      <Box>
        <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
          No documents uploaded.
        </Typography>
      </Box>
    );
  }

  const handleViewDocument = (documentUrl: string) => {
    window.open(documentUrl, '_blank');
  };

  const handleDownloadDocument = (documentUrl: string, fileName: string) => {
    // In a real application, this would trigger a download
    const link = document.createElement('a');
    link.href = documentUrl;
    link.download = fileName;
    link.click();
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(2) }}>
      {application.documents.map((document) => (
        <Box
          key={document.id}
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: theme.spacing(2),
            backgroundColor: theme.customColors.searchBackground,
            borderRadius: theme.shapes.borderRadius.md,
            border: `1px solid ${theme.customColors.tableBorder}`,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: theme.spacing(2), flex: 1 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: theme.shapes.borderRadius.md,
                backgroundColor: theme.palette.primary.main,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: theme.palette.primary.contrastText,
                fontSize: '1.25rem',
                fontWeight: 600,
              }}
            >
              PDF
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 500,
                  color: theme.palette.text.primary,
                  marginBottom: theme.spacing(0.5),
                }}
              >
                {documentTypeLabels[document.type] || document.type}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: theme.palette.text.secondary,
                  fontSize: '0.75rem',
                }}
              >
                {document.fileName} • Uploaded {document.uploadedAtText}
              </Typography>
            </Box>
            <Chip
              icon={document.verified ? <VerifiedIcon /> : <PendingIcon />}
              label={document.verified ? 'Verified' : 'Pending'}
              size="small"
              sx={{
                backgroundColor: document.verified
                  ? theme.palette.success.main
                  : theme.palette.warning.main,
                color: theme.palette.common.white,
                fontWeight: 500,
                fontSize: '0.75rem',
              }}
            />
          </Box>
          <Box sx={{ display: 'flex', gap: theme.spacing(1), marginLeft: theme.spacing(2) }}>
            <Tooltip title="View Document">
              <IconButton
                size="small"
                onClick={() => handleViewDocument(document.fileUrl)}
                sx={{ color: theme.palette.text.secondary }}
              >
                <ViewIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Download Document">
              <IconButton
                size="small"
                onClick={() => handleDownloadDocument(document.fileUrl, document.fileName)}
                sx={{ color: theme.palette.text.secondary }}
              >
                <DownloadIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      ))}
    </Box>
  );
};

